import React from 'react';
import "../styles/ecoFriendlySection.css";
import { ecoFriendlyData, getEcoFriendlyMessaging, getEcoFriendlyBenefits } from '../data/ecoData.js';

const EcoFriendlySection = ({ 
  layout = 'full', // 'full', 'compact', 'banner'
  showTitle = true,
  showCTA = true,
  variant = 'light', // 'light', 'dark', 'accent'
  context = 'residential', // 'residential', 'commercial', 'municipal'
  ctaOverrideLink = null // Override CTA link for blurb usage
}) => {
  // All destructuring inside the component function
  const { branding, practices, certifications } = ecoFriendlyData;
  const messaging = getEcoFriendlyMessaging(context);
  const allBenefits = getEcoFriendlyBenefits();

  // Better title options - you can update this in your data file
  const displayTitle = "Environmentally Safe Practices";
  const displaySubtitle = branding.tagline;

  // Banner layout
  if (layout === 'banner') {
    return (
      <div className={`eco-banner ${variant}`}>
        <div className="eco-banner-content">
          <div className="eco-banner-title">
            <span className="eco-title-text">
              {displayTitle}
            </span>
          </div>
          <div className="eco-banner-subtitle">
            {displaySubtitle}
          </div>
        </div>
      </div>
    );
  }

  // Compact layout
  if (layout === 'compact') {
    return (
      <section className={`eco-section compact ${variant}`}>
        <div className="eco-section-inner">
          {showTitle && (
            <div className={`eco-header compact`}>
              <h2 className={`eco-main-title compact ${variant}`}>
                {displayTitle}
              </h2>
              <p className="eco-description">
                Our eco-friendly approach protects the children, pets, and wildlife that call this valley home while maintaining beautiful, healthy landscapes.
              </p>
              <p className="eco-focus">
                Create stunning outdoor spaces that are safe for children and pets while protecting our environment
              </p>
            </div>
          )}
          
          <div className="eco-practices-grid compact">
            {practices.slice(0, 3).map((practice, index) => (
              <div key={practice.id} className={`eco-practice-card compact ${variant}`}>
                <div className="practice-icon">{practice.icon}</div>
                <h3 className="practice-title">
                  {practice.title}
                </h3>
                <p className="practice-short-desc">{practice.shortDescription}</p>
              </div>
            ))}
          </div>

          {/* CTA Section for Compact Layout */}
          {/* {showCTA && (
            <div className="eco-cta-section">
              <div className={`eco-cta-card ${variant}`}>
                <h3 className={`eco-cta-title ${variant}`}>
                  Learn More About Our Eco-Friendly Options
                </h3>
                <p className="eco-cta-desc">
                  Discover how we protect your family and the environment
                </p>
                <div className="eco-cta-buttons">
                  <a 
                    href={ctaOverrideLink || "/EcoPage"}
                    className="eco-cta-primary"
                  >
                    Explore Our Eco Practices
                    <span>→</span>
                  </a>
                </div>
              </div>
            </div>
          )} */}
        </div>
      </section>
    );
  }

  // Full layout
  return (
    <section className={`eco-section full ${variant}`}>
      <div className="eco-section-inner">
        {showTitle && (
          <div className="eco-header">
            <div className="eco-brand-badge">
              <span className="eco-badge-text">
                Eco-Friendly Landscaping
              </span>
            </div>
            <h2 className={`eco-main-title ${variant}`}>
              {displayTitle}
            </h2>
            <p className="eco-description">
              {branding.longDescription}
            </p>
            <p className="eco-focus">
              {messaging.focus}
            </p>
          </div>
        )}

        {/* Main Practices Grid */}
        <div className="eco-practices-grid full">
          {practices.map((practice) => (
            <div key={practice.id} className={`eco-practice-card full ${variant}`}>
              <div className="practice-icon">{practice.icon}</div>
              <h3 className="practice-title">
                {practice.title}
              </h3>
              <p className="practice-short-desc">
                {practice.shortDescription}
              </p>
              <p className="practice-long-desc">
                {practice.longDescription}
              </p>
              {practice.stats.reductionPercentage && (
                <p className="practice-stats">
                  {practice.stats.reductionPercentage}% reduction in {practice.stats.metric}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="eco-benefits-section">
          <div className={`eco-benefits-column ${variant}`}>
            <h3>Environmental Benefits</h3>
            <ul className="eco-benefits-list">
              {allBenefits.slice(0, 6).map((benefit, index) => (
                <li key={index}>
                  <span className="check-icon">✓</span>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className={`eco-benefits-column ${variant}`}>
            <h3>Our Certifications</h3>
            <div className="eco-certifications">
              {certifications.map((cert) => (
                <div key={cert.id} className={`eco-cert-card ${variant}`}>
                  <div className={`eco-cert-header ${variant}`}>
                    <span className="cert-icon">{cert.icon}</span>
                    <h4 className="cert-title">{cert.title}</h4>
                  </div>
                  <p className="eco-cert-desc">{cert.description}</p>
                  <p className="eco-cert-authority">{cert.authority}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        {showCTA && (
          <div className="eco-cta-section">
            <div className={`eco-cta-card ${variant}`}>
              <h3 className={`eco-cta-title ${variant}`}>
                Ask About Our Eco-Friendly Options
              </h3>
              <p className="eco-cta-desc">
                {ecoFriendlyData.callsToAction[0].description}
              </p>
              <div className="eco-cta-buttons">
                <a 
                  href={ctaOverrideLink || "mailto:info@allseasons.info"} 
                  className="eco-cta-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Request a Consultation
                  <span>→</span>
                </a>
                {/* <a 
                  href={ecoFriendlyData.callsToAction[1].link} 
                  className="eco-cta-primary"
                >
                  {ecoFriendlyData.callsToAction[1].text}
                  <span>→</span>
                </a> */}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

console.log("🧪 Exporting EcoFriendlySection, type is:", typeof EcoFriendlySection);
export default EcoFriendlySection;